import { AxiosError } from 'axios'

import { QueryKey, useQuery, UseQueryOptions } from '@tanstack/react-query'

import { IErrorDTO } from '@/DTOs/errorDTO'

import {
  getEducationStages,
  getPredictionsYears,
  getPredictionVersions,
  getPredictionsBaseYears,
  getCensusYears,
  getComparativeYears,
  getCitiesByUf,
  getSchoolsByCity,
} from './services'
import {
  TGetEducationStagesResponse,
  TGetPredictionsYearsResponse,
  TGetPredictionVersionsParams,
  TGetPredictionVersionsResponse,
  TGetPredictionsBaseYearsResponse,
  TGetCensusYearsResponse,
  TGetComparativeYearsResponse,
  TGetCitiesByUfParams,
  TGetCitiesByUfResponse,
  TGetSchoolsByCityParams,
  TGetSchoolsByCityResponse,
} from './types'

export const useQueryGetEducationStages = (
  options?: UseQueryOptions<TGetEducationStagesResponse, AxiosError<IErrorDTO>>
) => {
  const queryFunction = () => getEducationStages()

  return useQuery<TGetEducationStagesResponse, AxiosError<IErrorDTO>>(
    useQueryGetEducationStages.queryKey,
    queryFunction,
    options
  )
}

useQueryGetEducationStages.queryKey = ['education_stages']

export const useQueryGetPredictionsYears = (
  options?: UseQueryOptions<TGetPredictionsYearsResponse, AxiosError<IErrorDTO>>
) => {
  const queryFunction = () => getPredictionsYears()

  return useQuery<TGetPredictionsYearsResponse, AxiosError<IErrorDTO>>(
    useQueryGetPredictionsYears.queryKey,
    queryFunction,
    options
  )
}

useQueryGetPredictionsYears.queryKey = ['predictions_years']

export const useQueryGetPredictionVersions = (
  params: TGetPredictionVersionsParams,
  options?: UseQueryOptions<
    TGetPredictionVersionsResponse,
    AxiosError<IErrorDTO>
  >
) => {
  const queryFunction = () => getPredictionVersions(params)

  return useQuery<TGetPredictionVersionsResponse, AxiosError<IErrorDTO>>(
    [['prediction_versions'], params.nuCensusYear],
    queryFunction,
    { ...options, enabled: !!params.nuCensusYear }
  )
}

export const useQueryGetPredictionsBaseYears = (
  options?: UseQueryOptions<
    TGetPredictionsBaseYearsResponse,
    AxiosError<IErrorDTO>
  >
) => {
  const queryFunction = () => getPredictionsBaseYears()

  return useQuery<TGetPredictionsBaseYearsResponse, AxiosError<IErrorDTO>>(
    useQueryGetPredictionsBaseYears.queryKey,
    queryFunction,
    options
  )
}

useQueryGetPredictionsBaseYears.queryKey = ['predictions_base_years']

export const useQueryGetCensusYears = (
  options?: UseQueryOptions<TGetCensusYearsResponse, AxiosError<IErrorDTO>>
) => {
  const queryFunction = () => getCensusYears()

  return useQuery<TGetCensusYearsResponse, AxiosError<IErrorDTO>>(
    useQueryGetCensusYears.queryKey,
    queryFunction,
    options
  )
}

useQueryGetCensusYears.queryKey = ['census_years']

export const useQueryGetComparativeYears = (
  options?: UseQueryOptions<TGetComparativeYearsResponse, AxiosError<IErrorDTO>>
) => {
  const queryFunction = () => getComparativeYears()

  return useQuery<TGetComparativeYearsResponse, AxiosError<IErrorDTO>>(
    useQueryGetComparativeYears.queryKey,
    queryFunction,
    options
  )
}

useQueryGetComparativeYears.queryKey = ['comparative_years']

export const useQueryGetCitiesByUf = (
  params: TGetCitiesByUfParams,
  options?: UseQueryOptions<TGetCitiesByUfResponse, AxiosError<IErrorDTO>>
) => {
  const queryFunction = () => getCitiesByUf(params)

  return useQuery<TGetCitiesByUfResponse, AxiosError<IErrorDTO>>(
    [['cities'], params],
    queryFunction,
    options
  )
}

export const useQueryGetSchoolsByCity = (
  params: TGetSchoolsByCityParams,
  options?: UseQueryOptions<TGetSchoolsByCityResponse, AxiosError<IErrorDTO>>
) => {
  const queryFunction = () => getSchoolsByCity(params)

  return useQuery<TGetSchoolsByCityResponse, AxiosError<IErrorDTO>>(
    [['schools'], params],
    queryFunction,
    options
  )
}
